import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import toast, { Toaster, ToastBar } from 'react-hot-toast';

import Layout from './components/layout/Layout';
import { fetchSettings, selectSettings } from './store/slices/settingsSlice';

// Page Imports
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Auth from './pages/Auth';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import MyScripts from './pages/MyScripts';
import About from './pages/About';
import Contact from './pages/Contact';
import Wishlist from './pages/Wishlist';
import NotFound from './pages/NotFound';
import ReferAndEarn from './pages/ReferAndEarn';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DevelopmentMode from './pages/DevelopmentMode';
import Downloads from './pages/Downloads';
import Notifications from './pages/Notifications';
import ScriptRequests from './pages/ScriptRequests';

import ScrollToTop from './components/common/ScrollToTop';
import { resolveInternalRoute } from './utils/internalRouter';
import { LANGUAGE_MAP } from './utils/constants';

import './styles/globals.css';

// Deep Link Handler Component
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const processedRef = React.useRef(false); // Track if we've already handled a link

  React.useEffect(() => {
    // If already processed, stop immediately to prevent loops
    if (processedRef.current) return;

    let attempts = 0;
    const maxAttempts = 10; // 5 seconds (10 * 500ms)

    const checkDeepLink = () => {
      // Double check ref inside closure
      if (processedRef.current) return;

      // Helper to handle navigation
      const handleDeepLink = (param) => {
        if (!param) return false;

        // toast.success(`Redirecting to shared content...`); 
        const targetPath = resolveInternalRoute(param);
        if (targetPath) {
          processedRef.current = true;
          navigate(targetPath);
          return true;
        }

        return false;
      };

      // 1. Check for Telegram JS object start_param
      const tg = window.Telegram?.WebApp;
      if (tg?.initDataUnsafe?.start_param) {
        if (handleDeepLink(tg.initDataUnsafe.start_param)) return;
      }

      // 2. Fallback: Check for query params
      const urlParams = new URLSearchParams(window.location.search);
      const startParam =
        urlParams.get('route') ||
        urlParams.get('to') ||
        urlParams.get('page') ||
        urlParams.get('tgWebAppStartParam') ||
        urlParams.get('startapp');

      if (startParam) {
        if (handleDeepLink(startParam)) return;
      }

      // Retry if not found
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkDeepLink, 500);
      } else {
        // Stop checking silently after timeout
      }
    };

    // Start checking
    checkDeepLink();

    return () => {
      // Cleanup if needed
    };
  }, [navigate]);

  return null;
};


// Separated component to access useLocation context
// Separated component
const MainContent = () => {
  const settings = useSelector(selectSettings);
  const [isBypassed, setIsBypassed] = React.useState(localStorage.getItem('psh_dev_bypass') === 'true');

  React.useEffect(() => {
    // Check for bypass param
    const params = new URLSearchParams(window.location.search);
    if (params.get('dev') === 'true') {
      localStorage.setItem('psh_dev_bypass', 'true');
      setIsBypassed(true);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (settings?.developmentMode && !isBypassed) {
    return (
      <Routes>
        <Route path="*" element={<DevelopmentMode />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <AnimatedRoutes />
    </Layout>
  );
};

const AnimatedRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/:category" element={<Products />} />
      <Route path="/premium" element={<Products />} />
      <Route path="/exclusive" element={<Products />} />
      <Route path="/exclusives" element={<Products />} />
      <Route path="/bundles" element={<Products />} />
      <Route path="/bundle" element={<Products />} />
      <Route path="/free-scripts" element={<Products />} />
      <Route path="/free" element={<Products />} />
      <Route path="/others" element={<Products />} />
      <Route path="/other" element={<Products />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/p/:id" element={<ProductDetail />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/signup" element={<Auth />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/account" element={<Profile />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/my-scripts" element={<MyScripts />} />
      <Route path="/scripts" element={<MyScripts />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/wishlist" element={<Wishlist />} />
      <Route path="/refer-and-earn" element={<ReferAndEarn />} />
      <Route path="/refer" element={<ReferAndEarn />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route path="/download" element={<Downloads />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/notification" element={<Notifications />} />
      <Route path="/script-requests" element={<ScriptRequests />} />
      <Route path="/request-script" element={<ScriptRequests />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const TranslationInitializer = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { i18n } = useTranslation();

  React.useEffect(() => {
    if (isAuthenticated && user?.language) {
      const targetLang = LANGUAGE_MAP[user.language] || 'en';
      i18n.changeLanguage(targetLang);
      
      // Update HTML lang attribute
      document.documentElement.lang = targetLang;
      // Force LTR layout for all languages as requested
      document.documentElement.dir = 'ltr';
    }
  }, [isAuthenticated, user?.language, i18n]);

  return null;
};

function App() {
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  return (
    <div className="App min-h-screen bg-gray-50/80 dark:bg-gray-950">
      <TranslationInitializer />
      <DeepLinkHandler />
      <ScrollToTop />
      <MainContent />
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerStyle={{
          top: 20,
          right: 20,
        }}
        toastOptions={{
          duration: 2000,
          dismissible: true,
          style: {
            borderRadius: '12px',
            background: '#ffffff',
            color: '#1f2937',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e5e7eb',
            maxWidth: '350px',
            cursor: 'pointer',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
            style: {
              background: '#f0fdf4',
              color: '#166534',
              border: '1px solid #10B981',
              cursor: 'pointer',
            },
          },
          error: {
            duration: 2000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
            style: {
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #EF4444',
              cursor: 'pointer',
            },
          },
          loading: {
            duration: 2500,
            iconTheme: {
              primary: '#3B82F6',
              secondary: '#FFFFFF',
            },
            style: {
              background: '#eff6ff',
              color: '#1e40af',
              border: '1px solid #3B82F6',
              cursor: 'pointer',
            },
          },
        }}
      >
        {(t) => (
          <div
            onClick={() => toast.dismiss(t.id)}
            onTouchStart={() => toast.dismiss(t.id)}
            onPointerDown={() => toast.dismiss(t.id)}
            className="cursor-pointer select-none transition-transform active:scale-95"
            title="Dismiss notification"
          >
            <ToastBar toast={t} />
          </div>
        )}
      </Toaster>
    </div>
  );
}

export default App;
